// @flow

import * as React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, Pagination, IconButton } from "@components";
import { compose } from "redux";
import { formatDateTime } from "@transformers/date";
import type { EmployeeAbsenceInteraction } from "@modules/shared/reducer/employee-absences/types";
import actionCreators from "@modules/shared/actionCreator";
import {
  getInteractions,
  getInteractionsPagination,
} from "@modules/shared/selectors/employee-absences";
import type { PaginationExtendedType } from "@types";
import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  clientId: number,
  absenceId: number,
  employeeId: number,
  allowEdit: ?boolean,
  hideEditBtn: ?boolean,
  hideAction?: boolean,
}>;

type PropsFromState = $Exact<{
  interactions: Array<EmployeeAbsenceInteraction>,
  pagination: PaginationExtendedType,
}>;

type PropsFromDispatch = $Exact<{
  getInteractions: typeof actionCreators.GetEmployeeInteractions,
  toggleEditInteractionModal: typeof actionCreators.ToggleEditEmployeeInteractionModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AbsenceDetailsInteractionsTable extends React.Component<Props> {
  componentDidMount() {
    this.props.getInteractions({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
    });
  }

  showEditInteractionModal = (interaction: EmployeeAbsenceInteraction) => () => {
    this.props.toggleEditInteractionModal({
      visible: true,
      data: interaction,
    });
  };

  handlePaginationChange = (page: number) => {
    this.props.getInteractions({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      page,
    });
  };

  get columns() {
    let result = [
      {
        name: this.props.t("DATE_AND_TIME"),
        renderCell: ({ row }: { row: EmployeeAbsenceInteraction }) => formatDateTime(row.updatedAt),
        key: "updatedAt",
        width: "min-content",
        align: "left",
      },
      {
        name: this.props.t("ADVISOR"),
        renderCell: ({ row }: { row: EmployeeAbsenceInteraction }) =>
          row.updatedBy ? `${row.updatedBy.firstName} ${row.updatedBy.lastName}` : "",
        key: "advisor",
        width: "min-content",
        align: "left",
      },
      {
        name: this.props.t("INTERACTION_NOTES"),
        key: "notes",
        align: "left",
      },
      {
        name: this.props.t("PURE_CLOUD_INTERACTION_LINK"),
        align: "left",
        key: "pureCloudUrl",
        minWidth: 170,
        renderCell: ({ row }: { row: EmployeeAbsenceInteraction }) => {
          if (row.pureCloudUrl) {
            return (
              <a href={row.pureCloudUrl} target="_blank" rel="noopener noreferrer">
                {this.props.t("INTERACTION_LINK")}
              </a>
            );
          }

          return "";
        },
      },
      {
        name: this.props.t("EDIT"),
        key: "actions",
        hidden: this.props.hideAction,
        width: "min-content",
        renderCell: ({ row }: { row: EmployeeAbsenceInteraction }) => {
          const isDisabled =
            (row.createdBy || {}).id !== this.props.employeeId && !this.props.allowEdit;
          return (
            <IconButton
              icon={
                <span
                  className={`material-icons pointer text-color-${
                    isDisabled ? "disabled" : "black"
                  }`}
                >
                  edit
                </span>
              }
              disabled={isDisabled}
              onClick={this.showEditInteractionModal(row)}
            />
          );
        },
      },
    ];

    if (this.props.hideEditBtn) {
      result.pop();
    }
    return result;
  }

  render() {
    return (
      <React.Fragment>
        <GSGrid rows={this.props.interactions} columns={this.columns} />
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={this.props.pagination.pageNumber}
          totalPages={this.props.pagination.totalPages}
          onChange={this.handlePaginationChange}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  interactions: getInteractions(state),
  pagination: getInteractionsPagination(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getInteractions: actionCreators.GetEmployeeInteractions,
  toggleEditInteractionModal: actionCreators.ToggleEditEmployeeInteractionModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AbsenceDetailsInteractionsTable);
